import { useComplianceCalendarEvents } from '@commenda/api';
import { NonTimezoneSensitiveDate } from '@commenda/utils';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo } from 'react';
import AgendaSkeleton from './AgendaSkeleton';
import { CalendarViewMode, RbcEvent } from './calendar.types';
import CalendarAgendaView from './CalendarAgendaView';
import { CalendarNavContext } from './CalendarContext';
import { adaptEvents } from './calendarEventAdapter';
import {
  getMonthGridRows,
  getViewDateRange,
  getViewLabel,
  groupEventsByDate,
  navigateDate,
} from './calendarGrid';
import CalendarMonthGrid from './CalendarMonthGrid';
import { navigateToEventDetail } from './calendarNavigation';
import CalendarSkeleton from './CalendarSkeleton';
import CalendarToolbar from './CalendarToolbar';

interface ComplianceCalendarViewProps {
  companyId: number;
  filterEvents?: (events: RbcEvent[]) => RbcEvent[];
  onEventsChange?: (events: RbcEvent[]) => void;
  refreshTrigger?: number;
  /** View used when the URL carries no `calendarView`. Omit to keep the agenda default. */
  initialView?: CalendarViewMode;
  /** ISO-8601 date used when the URL carries no valid `calendarDate`. Omit to keep today. */
  initialDate?: string;
}

const ComplianceCalendarView = ({
  companyId,
  filterEvents,
  onEventsChange,
  refreshTrigger,
  initialView,
  initialDate,
}: ComplianceCalendarViewProps) => {
  const router = useRouter();

  // Derive view from URL or fallback to default
  const urlView = router.query.calendarView as CalendarViewMode | undefined;
  const view: CalendarViewMode =
    urlView && ['month', 'agenda'].includes(urlView)
      ? urlView
      : (initialView ?? 'agenda');

  // The date to land on when the URL says nothing usable. A malformed prop degrades to today
  // rather than throwing, so a bad caller cannot break the calendar for everyone else.
  const fallbackDate: string = useMemo(() => {
    if (initialDate) {
      try {
        return NonTimezoneSensitiveDate.fromISO8601String(
          initialDate
        ).toISO8601String();
      } catch {
        return NonTimezoneSensitiveDate.now().toISO8601String();
      }
    }
    return NonTimezoneSensitiveDate.now().toISO8601String();
  }, [initialDate]);

  // Derive date from URL or fallback to default
  const urlDate = router.query.calendarDate as string | undefined;
  const date: string = useMemo(() => {
    if (urlDate) {
      try {
        // Round-trip to validate/normalize; toISO8601String throws on a malformed date.
        return NonTimezoneSensitiveDate.fromISO8601String(
          urlDate
        ).toISO8601String();
      } catch {
        // Invalid date in URL, fallback to default
        return fallbackDate;
      }
    }
    return fallbackDate;
  }, [urlDate, fallbackDate]);

  const onDateSet = useCallback(
    (newDate: string) => {
      router.replace(
        {
          query: {
            ...router.query,
            calendarDate: newDate,
            calendarView: view,
          },
        },
        undefined,
        { shallow: true }
      );
    },
    [router, view]
  );

  const onViewChange = useCallback(
    (newView: CalendarViewMode) => {
      router.replace(
        {
          query: {
            ...router.query,
            calendarDate: date,
            calendarView: newView,
          },
        },
        undefined,
        { shallow: true }
      );
    },
    [router, date]
  );

  const dateRange = useMemo(() => getViewDateRange(view, date), [view, date]);

  const { dates, isLoading, calendarNotGenerated, mutate } =
    useComplianceCalendarEvents({
      companyId,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      enabled: !!companyId,
      includeHiddenComplianceTypes: true,
    });

  const allEvents = useMemo(() => adaptEvents(dates), [dates]);
  const filteredEvents = filterEvents ? filterEvents(allEvents) : allEvents;
  const eventsByDate = useMemo(
    () => groupEventsByDate(filteredEvents),
    [filteredEvents]
  );

  useEffect(() => {
    if (!isLoading) onEventsChange?.(allEvents);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, dates]);

  useEffect(() => {
    if (!refreshTrigger) return;
    mutate();
  }, [refreshTrigger]);

  const onSelectEvent = useCallback(
    (event: RbcEvent) => {
      navigateToEventDetail(event.resource, router);
    },
    [router]
  );

  const navigateToWeek = useCallback(
    (targetDate: Date) => {
      onViewChange('agenda');
      onDateSet(
        NonTimezoneSensitiveDate.fromNativeDate(targetDate).toISO8601String()
      );
    },
    [onViewChange, onDateSet]
  );

  const onNavigate = useCallback(
    (direction: 'PREV' | 'NEXT' | 'TODAY') => {
      onDateSet(navigateDate(date, direction));
    },
    [view, date, onDateSet]
  );

  const label = useMemo(() => getViewLabel(view, date), [view, date]);
  const monthRows = useMemo(
    () =>
      view === 'month'
        ? getMonthGridRows(
            NonTimezoneSensitiveDate.fromISO8601String(date).toDate()
          )
        : [],
    [view, date]
  );

  const calendarNavValue = useMemo(
    () => ({ navigateToWeek }),
    [navigateToWeek]
  );

  return (
    <CalendarNavContext.Provider value={calendarNavValue}>
      <div className="flex flex-col flex-1 min-h-0">
        <CalendarToolbar
          view={view}
          label={label}
          onPrev={() => onNavigate('PREV')}
          onNext={() => onNavigate('NEXT')}
          onToday={() => onNavigate('TODAY')}
          onView={onViewChange}
        />
        {isLoading ? (
          view === 'month' ? (
            <CalendarSkeleton />
          ) : (
            <AgendaSkeleton />
          )
        ) : view === 'month' ? (
          <CalendarMonthGrid
            rows={monthRows}
            currentMonth={NonTimezoneSensitiveDate.fromISO8601String(
              date
            ).toDate()}
            eventsByDate={eventsByDate}
            onSelectEvent={onSelectEvent}
          />
        ) : (
          <CalendarAgendaView
            eventsByDate={eventsByDate}
            currentMonth={date}
            onSelectEvent={onSelectEvent}
            calendarNotGenerated={calendarNotGenerated}
            onFilingMutated={mutate}
          />
        )}
      </div>
    </CalendarNavContext.Provider>
  );
};

export default ComplianceCalendarView;
